import { BasePlugin, IBaseConfig, IEventDetail, IPlugin } from "@easepick/base-plugin";
import { DateTime } from "@easepick/datetime";
import { LockPlugin } from "@easepick/bundle";

declare module "@easepick/core/dist/types" {
    interface IPickerConfig {
        MonthPickerPlugin?: IMonthPickerConfig;
    }
}

export interface IMonthPickerConfig extends IBaseConfig {
    minYear?: number;
    maxYear?: number;
    resetButton?: boolean;
    locale?: {
        resetButton?: string;
    };
}

class MonthPickerPlugin extends BasePlugin implements IPlugin {
    render: (event: CustomEvent) => void;

    lockPlugin: LockPlugin;

    options: IMonthPickerConfig = {
        minYear: 1900,
        maxYear: null,
        resetButton: null,
        locale: {
            resetButton:
                '<svg xmlns="http://www.w3.org/2000/svg" height="24" width="24"><path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/></svg>'
        }
    };

    getName() {
        return "MonthPickerPlugin";
    }

    onAttach(): void {
        this.render = this.onRender.bind(this);

        this.lockPlugin = this.picker.PluginManager.getInstance("LockPlugin");
        this.picker.on("render", this.render);
    }

    onDetach(): void {
        this.picker.off("render", this.render);
    }

    onRender(event: CustomEvent) {
        const { detail } = event;
        const { view, date } = detail as IEventDetail;
        if (view === "Container") {
            const container = this.picker.ui.container;
            const calendar = container.querySelector<HTMLElement>(".calendars > .calendar");
            this.removeDaysGrid(container);
            this.getYearSelectionView(date, calendar);
            this.getCalendarMonthView(event, date, calendar);
            if (this.options.resetButton) {
                this.getResetButton();
            }
        }
    }

    getResetButton() {
        const container = this.picker.ui.container;
        const footerButtons = container.querySelector<HTMLElement>(".footer-buttons");

        const reset = document.createElement("button");
        reset.classList.add("reset-button");
        reset.classList.add("unit");

        reset.innerHTML = this.options.locale.resetButton;

        footerButtons.insertAdjacentElement("afterbegin", reset);

        reset.addEventListener("click", () => {
            this.picker.clear();
        });
    }

    getYearSelectionView(date: DateTime, calendar: HTMLElement) {
        const years = document.createElement("div");
        years.classList.add("years");

        const minYear = this.options.minYear;
        const maxYear = this.options.maxYear ?? new Date().getFullYear();
        const currentYear = date.getFullYear();

        years.setAttribute("name", "years");
        years.setAttribute("placeholder", "Year");

        // Helper function to create year element
        const createYearElement = (year: number, isDisabled = false) => {
            const yearElement = document.createElement("span");
            const time = new DateTime(new Date(year, 0, 1)).getTime();
            yearElement.classList.add("year");
            yearElement.dataset.year = String(year);
            yearElement.dataset.time = String(time);
            yearElement.innerHTML = String(year);
            yearElement.classList.toggle("selected", currentYear === year);
            if (isDisabled) yearElement.classList.add("disabled");
            return yearElement;
        };

        // Add year above max if current year exceeds max
        if (currentYear > maxYear) {
            years.appendChild(createYearElement(currentYear, true));
        }

        // Add all years in range (from max to min)
        for (let i = maxYear; i >= minYear; i -= 1) {
            years.appendChild(createYearElement(i));
        }

        // Add year below min if current year is below min
        if (currentYear < minYear) {
            years.appendChild(createYearElement(currentYear, true));
        }

        calendar.prepend(years);

        years.querySelectorAll(".year").forEach((year: HTMLElement) => {
            year.addEventListener("click", (e) => {
                this.onClickYear(e.target as HTMLElement);
            });

            if (year.classList.contains("selected")) {
                years.scrollTop = year.offsetTop - years.offsetHeight / 2;
            }
        });
    }

    getCalendarMonthView(event: CustomEvent, date: DateTime, calendar: HTMLElement) {
        const years = calendar.querySelector<HTMLElement>(".years");
        const monthsGrid = document.createElement("div");
        monthsGrid.className = "months-grid";

        const selectedDate = this.picker.options.date ? new DateTime(this.picker.options.date) : null;
        const now = new DateTime();

        for (let i = 0; i < 12; i += 1) {
            const month = document.createElement("div");
            month.className = "month unit";
            const firstDayOfMonth = new DateTime(new Date(date.getFullYear(), i, 1, 0, 0, 0));

            if (this.lockPlugin) {
                if (
                    (this.lockPlugin.options.minDate &&
                        firstDayOfMonth.isBefore(new DateTime(this.lockPlugin.options.minDate), "month")) ||
                    (this.lockPlugin.options.maxDate &&
                        firstDayOfMonth.isAfter(new DateTime(this.lockPlugin.options.maxDate), "month"))
                ) {
                    month.classList.add("locked");
                }
            }

            month.innerHTML = firstDayOfMonth.toLocaleString(this.picker.options.lang, { month: "short" });
            month.dataset.month = String(i + 1).padStart(2, "0");
            month.dataset.time = String(firstDayOfMonth.getTime());

            if (firstDayOfMonth.isSame(now, "month")) {
                month.classList.add("today");
            }

            // Check if this month should be selected
            const pickedDate = this.picker.datePicked.length > 0 ? this.picker.datePicked[0] : selectedDate;
            if (pickedDate && firstDayOfMonth.isSame(pickedDate, "month")) {
                month.classList.add("selected");
            }

            this.picker.trigger("view", {
                date: firstDayOfMonth,
                view: "CalendarMonth",
                target: month
            });

            month.addEventListener("click", (e) => {
                const target = e.target as HTMLElement;
                this.onClickCalendarMonth(target);
            });

            monthsGrid.appendChild(month);
        }

        years.after(monthsGrid);
    }

    onClickCalendarMonth(target: HTMLElement) {
        if (!this.isCalendarMonth(target)) return;

        const date = new DateTime(target.dataset.time);
        const calendar = this.picker.calendars[0];

        // Update calendar view
        calendar.setFullYear(date.getFullYear());
        calendar.setMonth(date.getMonth());
        calendar.setDate(1);

        if (this.picker.options.autoApply) {
            this.picker.setDate(date);
            this.picker.trigger("select", { date: this.picker.getDate() });
            this.picker.hide();
        } else {
            this.picker.datePicked[0] = date;
            this.picker.trigger("preselect", { date: this.picker.getDate() });
            this.picker.renderAll();
        }
    }

    onClickYear(target: HTMLElement) {
        const selectedYear = Number(target.dataset.year);
        const calendar = this.picker.calendars[0];
        const currentSelectedDate =
            this.picker.datePicked.length > 0 ? this.picker.datePicked[0] : this.picker.getDate();
        const currentMonth = currentSelectedDate ? currentSelectedDate.getMonth() : calendar.getMonth();

        // Update calendar view to new year with preserved month
        calendar.setFullYear(selectedYear);
        calendar.setMonth(currentMonth);
        calendar.setDate(1);

        // Preserve month selection if a date is currently selected
        if (currentSelectedDate) {
            const newDate = new DateTime(new Date(selectedYear, currentSelectedDate.getMonth(), 1));
            this.picker.datePicked[0] = newDate;
            this.picker.trigger("preselect", { date: newDate });
        }

        this.picker.renderAll();
    }

    removeDaysGrid(container: HTMLElement) {
        if (!container) return;

        container.querySelector(".header")?.remove();
        container.querySelector(".days-grid")?.remove();
        container.querySelector(".daynames-row")?.remove();
        container.querySelector(".footer")?.remove();
    }

    isDayName(target: HTMLElement) {
        return target.classList.contains("dayname");
    }

    isCalendarMonth(target: HTMLElement) {
        return target.classList.contains("month");
    }
}

export { MonthPickerPlugin };
